/**
 * 画布级 Promise 确认弹窗：任何模块可 `await boardConfirm({ title, message })`
 * 获取用户选择，避免各入口各自维护一份弹窗状态。
 *
 * BoardWorkspace 挂载一次；调用均为单发手势（删除整批确认），并发时新请求
 * 顶掉旧请求、旧 Promise 按取消结算，不会悬挂。
 * 打开期间置 body.boardModalOpen，画布快捷键（删除/撤销等）让位，
 * 与 MaskDialog 同一约定。
 */
import { useEffect, useSyncExternalStore } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

interface PendingConfirm {
  title: string;
  message: string;
  resolve: (ok: boolean) => void;
}

let pending: PendingConfirm | null = null;
let emit: (() => void) | null = null;

export function boardConfirm(opts: { title: string; message: string }): Promise<boolean> {
  return new Promise((resolve) => {
    pending?.resolve(false);
    pending = { ...opts, resolve };
    emit?.();
  });
}

function settle(ok: boolean) {
  pending?.resolve(ok);
  pending = null;
  emit?.();
}

export function BoardConfirm() {
  const snap = useSyncExternalStore(
    (cb) => {
      emit = cb;
      return () => {
        if (emit === cb) emit = null;
      };
    },
    () => pending
  );

  useEffect(() => {
    if (!snap) return;
    document.body.dataset.boardModalOpen = 'true';
    return () => {
      delete document.body.dataset.boardModalOpen;
    };
  }, [snap]);

  if (!snap) return null;
  return (
    <ConfirmDialog
      open
      title={snap.title}
      message={snap.message}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
}
